import type { paths } from "../deferred-api/contracts";
import { CyraError } from "./errors";
import type { LegalDocument } from "./models";

export type CurrentLegalDocuments =
  paths["/v1/legal/current"]["get"]["responses"][200]["content"]["application/json"];

function unavailable(): never {
  throw new CyraError({
    code: "SYSTEM_NOT_READY",
    message: "The current legal documents are unavailable.",
    requestId: "current-legal-documents",
    status: 503,
  });
}

export function parsePublicLegalDocuments(value: unknown): CurrentLegalDocuments {
  if (!value || typeof value !== "object") return unavailable();
  const pair = value as Record<string, unknown>;
  const document = (value: unknown, kind: "PRIVACY" | "TERMS") => {
    if (!value || typeof value !== "object") return unavailable();
    const item = value as Record<string, unknown>;
    const { body, effectiveAt, id, title, version } = item;
    if (
      item["kind"] !== kind ||
      typeof body !== "string" ||
      typeof effectiveAt !== "number" ||
      !Number.isFinite(effectiveAt) ||
      typeof id !== "string" ||
      !id ||
      typeof title !== "string" ||
      typeof version !== "string" ||
      !version
    )
      return unavailable();
    return { body, effectiveAt, id, kind, title, version };
  };
  return { privacy: document(pair["privacy"], "PRIVACY"), terms: document(pair["terms"], "TERMS") };
}

export function projectCurrentLegalDocuments(
  documents: readonly LegalDocument[],
): CurrentLegalDocuments {
  const current = (kind: "privacy" | "terms") => {
    const matches = documents.filter((item) => item.kind === kind && item.current);
    const item = matches[0];
    if (matches.length !== 1 || !item || item.state !== "PUBLISHED" || item.publishedAt === null)
      return unavailable();
    return {
      body: item.bodyHtml,
      effectiveAt: item.effectiveAt,
      id: item.id,
      kind: kind === "privacy" ? "PRIVACY" : "TERMS",
      title: item.title,
      version: item.version,
    };
  };
  return parsePublicLegalDocuments({ privacy: current("privacy"), terms: current("terms") });
}
